import { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useClientUser } from '@/hooks';
import { UserContext } from '@/components/App';
import { CLIENT_BY_ID } from '@/graphql/queries/users';
import { RESOURCE_SCHEDULE_CUTOFF } from '@/constants/resourceScheduling';
import { asContextClient } from '@/components/CorporateShipper/utils';
import { GET_ORG_CARRIER_RELATIONSHIP } from './graphql';

/**
 * Loads a carrier for a corporate admin and builds the UserContext its tabs run in. Call it from the admin's context;
 * `carrierContext` swaps `client` to the carrier.
 *
 * The carrier is permitted when it has a row in the org's organization_carrier_statuses, so Fulfillment Partners and
 * Motor Carriers the org doesn't own open too. `admin_clients` only holds the org's own clients. Only a corporate admin
 * gets an org here, so a carrier login in the org is never permitted.
 * @param {string} client_id
 */
export const useCarrierScope = (client_id) => {
    const outer = useContext(UserContext);
    const { organization_id, isCorporateAdmin } = useClientUser();
    const organizationId = isCorporateAdmin ? organization_id : null;

    const membership = useQuery(GET_ORG_CARRIER_RELATIONSHIP, {
        variables: { organization_id: organizationId, carrier_id: client_id },
        skip: !organizationId || !client_id,
    });
    const relationship = membership.data?.organization_carrier_statuses?.[0]?.relationship;
    const isPermitted = !!relationship;

    const { data, loading, error } = useQuery(CLIENT_BY_ID, {
        variables: { client_id, cutoff: RESOURCE_SCHEDULE_CUTOFF },
        skip: !isPermitted,
    });

    const carrier = data?.client;

    const carrierContext = useMemo(
        () => ({ ...outer, client: asContextClient(carrier), clientId: carrier?.client_id }),
        [outer, carrier]
    );

    return {
        organizationId,
        relationship,
        isPermitted,
        carrier,
        loading: membership.loading || loading,
        error: membership.error || error,
        carrierContext,
    };
};
